import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { 
  Users, 
  Tv, 
  UserCheck, 
  UserX, 
  Clock,
  Settings,
  BarChart3,
  Shield,
  LogOut,
  Eye,
  Plus
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [recentChannels, setRecentChannels] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, usersRes, channelsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/users/pending`),
        axios.get(`${API}/admin/channels`)
      ]);

      setStats(statsRes.data);
      setPendingUsers(usersRes.data.slice(0, 5)); // Last 5 pending users
      setRecentChannels(channelsRes.data.slice(-5).reverse()); // Last 5 channels
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Erro ao carregar dados do dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleQuickApproval = async (userId, action) => {
    try {
      await axios.put(`${API}/admin/users/${userId}`, {
        status: action === 'approve' ? 'approved' : 'rejected'
      });
      
      toast.success(`Usuário ${action === 'approve' ? 'aprovado' : 'rejeitado'} com sucesso!`);
      fetchDashboardData(); // Refresh data
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Erro ao atualizar usuário');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 flex items-center justify-center">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4" />
          <p className="text-white">Carregando dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900">
      {/* Header */}
      <header className="bg-black/20 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Shield className="h-8 w-8 text-red-400" />
                <span className="text-xl font-bold text-white">ADMIN PANEL</span>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2 text-white">
                <span className="hidden sm:inline">Admin: {user?.full_name}</span>
              </div>
              <Link to="/dashboard">
                <Button variant="outline" size="sm" className="text-white border-white/20">
                  <Eye className="h-4 w-4 mr-2" />
                  Ver Site
                </Button>
              </Link>
              <Button
                variant="outline"
                size="sm"
                onClick={logout}
                className="text-white border-white/20 hover:bg-white/10"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Sair
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">
            Dashboard Administrativo
          </h1>
          <p className="text-gray-300">
            Gerencie usuários, canais e monitore a plataforma
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="bg-white/5 border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-300">Total de Usuários</p>
                  <p className="text-3xl font-bold text-white">{stats?.total_users || 0}</p>
                </div>
                <Users className="h-8 w-8 text-blue-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white/5 border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-300">Pendentes</p>
                  <p className="text-3xl font-bold text-yellow-400">{stats?.pending_users || 0}</p>
                </div>
                <Clock className="h-8 w-8 text-yellow-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white/5 border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-300">Aprovados</p>
                  <p className="text-3xl font-bold text-green-400">{stats?.approved_users || 0}</p>
                </div>
                <UserCheck className="h-8 w-8 text-green-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white/5 border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-300">Canais Ativos</p>
                  <p className="text-3xl font-bold text-purple-400">{stats?.active_channels || 0}</p>
                </div>
                <Tv className="h-8 w-8 text-purple-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-8">
          <Card className="bg-white/5 border-white/10">
            <CardHeader>
              <CardTitle className="text-white flex items-center">
                <Users className="mr-2 h-5 w-5" />
                Gerenciar Usuários
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-gray-300 text-sm">
                Aprove, rejeite ou gerencie contas de usuários
              </p>
              <Link to="/admin/users">
                <Button className="w-full bg-blue-600 hover:bg-blue-700">
                  Gerenciar Usuários
                </Button>
              </Link>
            </CardContent>
          </Card>

          <Card className="bg-white/5 border-white/10">
            <CardHeader>
              <CardTitle className="text-white flex items-center">
                <Tv className="mr-2 h-5 w-5" />
                Gerenciar Canais
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-gray-300 text-sm">
                Adicione, edite ou remova canais da plataforma
              </p>
              <Link to="/admin/channels">
                <Button className="w-full bg-purple-600 hover:bg-purple-700">
                  Gerenciar Canais
                </Button>
              </Link>
            </CardContent>
          </Card>

          {user?.role === 'superadmin' && (
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center">
                  <Settings className="mr-2 h-5 w-5" />
                  White Label
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-300 text-sm">
                  Personalize marca, cores e aparência da plataforma
                </p>
                <Link to="/superadmin/settings">
                  <Button className="w-full bg-red-600 hover:bg-red-700">
                    Configurar Plataforma
                  </Button>
                </Link>
              </CardContent>
            </Card>
          )}

          <Card className="bg-white/5 border-white/10">
            <CardHeader>
              <CardTitle className="text-white flex items-center">
                <BarChart3 className="mr-2 h-5 w-5" />
                Relatórios
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-gray-300 text-sm">
                Visualize estatísticas e relatórios da plataforma
              </p>
              <Button className="w-full bg-green-600 hover:bg-green-700" disabled>
                Em Breve
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Pending Users */}
          <Card className="bg-white/5 border-white/10">
            <CardHeader>
              <CardTitle className="text-white flex items-center justify-between">
                <span className="flex items-center">
                  <Clock className="mr-2 h-5 w-5" />
                  Usuários Pendentes
                </span>
                <Badge variant="secondary" className="bg-yellow-500/20 text-yellow-300">
                  {stats?.pending_users || 0}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {pendingUsers.length === 0 ? (
                <p className="text-gray-400 text-center py-4">
                  Nenhum usuário pendente
                </p>
              ) : (
                <div className="space-y-4">
                  {pendingUsers.map((user) => (
                    <div key={user.id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                      <div>
                        <p className="text-white font-medium">{user.full_name}</p>
                        <p className="text-gray-400 text-sm">{user.email}</p>
                      </div>
                      <div className="flex space-x-2">
                        <Button
                          size="sm"
                          onClick={() => handleQuickApproval(user.id, 'approve')}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <UserCheck className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleQuickApproval(user.id, 'reject')}
                        >
                          <UserX className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {pendingUsers.length > 0 && (
                    <Link to="/admin/users">
                      <Button variant="outline" className="w-full text-white border-white/20">
                        Ver Todos
                      </Button>
                    </Link>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Channels */}
          <Card className="bg-white/5 border-white/10">
            <CardHeader>
              <CardTitle className="text-white flex items-center justify-between">
                <span className="flex items-center">
                  <Tv className="mr-2 h-5 w-5" />
                  Canais Recentes
                </span>
                <Link to="/admin/channels">
                  <Button size="sm" className="bg-purple-600 hover:bg-purple-700">
                    <Plus className="h-4 w-4 mr-1" />
                    Novo
                  </Button>
                </Link>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {recentChannels.length === 0 ? (
                <p className="text-gray-400 text-center py-4">
                  Nenhum canal cadastrado
                </p>
              ) : (
                <div className="space-y-4">
                  {recentChannels.map((channel) => (
                    <div key={channel.id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded flex items-center justify-center">
                          <Tv className="h-4 w-4 text-white" />
                        </div>
                        <div>
                          <p className="text-white font-medium">{channel.name}</p>
                          <p className="text-gray-400 text-sm">{channel.category}</p>
                        </div>
                      </div>
                      <Badge 
                        variant="secondary" 
                        className={channel.is_active 
                          ? "bg-green-500/20 text-green-300" 
                          : "bg-red-500/20 text-red-300"
                        }
                      >
                        {channel.is_active ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </div>
                  ))}
                  <Link to="/admin/channels">
                    <Button variant="outline" className="w-full text-white border-white/20">
                      Ver Todos
                    </Button>
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;